import type { CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { villageTagsQueryOptions } from "../../services/queries/publicQueryOptions";
import type { VillageSectionProps } from "./types";
import { buildReferenceFactRows } from "./utils";
import { VillageIncidentChartsPanel } from "../incidents/components/VillageIncidentChartsPanel";
import { VillageEvidenceGalleryCard } from "./components/VillageEvidenceGalleryCard";
import { VillageSatelliteBeforeAfterSection } from "./components/VillageSatelliteBeforeAfterSection";
import { useVillageSatelliteComparison } from "./hooks/useVillageSatelliteComparison";

export function VillageSection({
  village,
  interviewees,
  evidenceItems,
  isIntervieweesLoading,
  isEvidenceLoading,
  villageFacts,
  intervieweeDirectorySummary,
  incidentEntries,
  incidentOverview,
  evidenceEntries,
  onSelectInterviewee,
  onLoadMoreInterviewees,
  canLoadMoreInterviewees,
  isLoadingMoreInterviewees,
  onToggleIncidentOverview,
  returnTo,
  onBack,
}: VillageSectionProps) {
  const hasPreviewMatch = Boolean(village?.backendRef);
  const referenceFactRows = buildReferenceFactRows(village);
  const satelliteVillageRef = village?.reference?.id ?? village?.reference?.slug ?? village?.backendRef ?? village?.slug ?? null;
  const satelliteComparison = useVillageSatelliteComparison(satelliteVillageRef);

  const villageHeroStyle = satelliteComparison.heroImageUrl
    ? ({ "--village-hero-image": `url(\"${satelliteComparison.heroImageUrl}\")` } as CSSProperties)
    : undefined;

  const backLabel = returnTo === "villagesList" ? "Back to Villages" : "Back to Map";
  const backButton = (
    <button type="button" className="nav-back-button" onClick={onBack}>
      <span className="nav-back-button__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" role="presentation">
          <path d="M14.4 6.4L9 11.8l5.4 5.4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{backLabel}</span>
    </button>
  );

  const tagsQuery = useQuery(villageTagsQueryOptions(village?.backendRef ?? null));
  const tags = tagsQuery.data?.tags ?? [];

  if (village && !hasPreviewMatch) {
    return (
      <section className="profile-view" aria-labelledby="village-profile-title">
        <div className="profile-view__inner">
          {backButton}
          <h1 id="village-profile-title" className="profile-view__heading">{village.title}, {village.township || "Unknown"}</h1>

          <div className="frame frame-village">
            <article className="box village-hero fade-in" style={villageHeroStyle}>
              <div className="village-hero__summary">
                <h3>Summary</h3>
                {referenceFactRows.length > 0 ? (
                  <dl className="detail-pairs detail-pairs-stack">
                    {referenceFactRows.map((row) => (
                      <div key={row.label}>
                        <dt>{row.label}:</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="small-paragraph">No published village metadata is available for this location yet.</p>
                )}
              </div>
            </article>
          </div>

          <div className="frame frame-village">
            <VillageSatelliteBeforeAfterSection model={satelliteComparison} villageName={village.title} />
          </div>

          {backButton}
        </div>
      </section>
    );
  }

  return (
    <section className="profile-view" aria-labelledby="village-profile-title">
      <div className="profile-view__inner">
        {backButton}
        <h1 id="village-profile-title" className="profile-view__heading">{village ? `${village.title}, ${village?.township ?? ""}` : "No village to preview"}</h1>

        <div className="frame frame-village">
          <article className="box village-hero fade-in" style={villageHeroStyle}>
            <div className="village-hero__summary">
              <h3>Summary</h3>
              <dl className="detail-pairs detail-pairs-stack">
                {/* <div><dt>Location:</dt><dd>{village?.title ?? "-"}</dd></div>
                <div><dt>Township:</dt><dd>{village?.township ?? "-"}</dd></div> */}
                {village?.reference?.myanmarName && (
                  <div><dt>Myanmar Name:</dt><dd>{village.reference.myanmarName}</dd></div>
                )}
                <div><dt>Status 2019:</dt><dd>{villageFacts?.status2019 ?? "-"}</dd></div>
                <div><dt>Survivor Accounts:</dt><dd>{village?.stats.survivorAccountCount ?? 0}</dd></div>
                <div><dt>Evidence Items:</dt><dd>{village?.stats.evidenceItemCount ?? 0}</dd></div>
              </dl>
            </div>
          </article>

          <VillageSatelliteBeforeAfterSection model={satelliteComparison} villageName={village?.title ?? null} />

          <div className="village-highlight-grid">
            <article className="box compact-box fade-in">
              <h3>Reported Human Rights Violations</h3>
              {tags.length > 0 ? (
                <div className="violation-grid">
                  {tags.map((item) => (
                    <div key={item.tag} className="violation-card">
                      <span className="violation-name">{item.label}</span>
                      <span className="violation-count">{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="small-paragraph">No published human rights violation tags are available for this village yet.</p>
              )}
            </article>
          </div>

          <VillageIncidentChartsPanel interviewees={interviewees} evidenceItems={evidenceItems} />

          <VillageEvidenceGalleryCard villageRef={satelliteVillageRef} evidenceItems={evidenceItems} isEvidenceLoading={isEvidenceLoading} />

          <article className="box interviewee-box fade-in">
            <div className="box-title-row">
              <h3>Survivors</h3>
              <span className="view-all-link">{intervieweeDirectorySummary}</span>
            </div>

            <div className="mini-card-grid">
              {interviewees.map((interviewee) => (
                <button key={interviewee.id} type="button" className="mini-card interactive-card" onClick={() => onSelectInterviewee(interviewee.id)}>
                  <div className="mini-card-thumb"><img src="/profile.png" alt="Interviewee placeholder" loading="lazy" /></div>
                  <div className="mini-card-body">
                    <p className="card-row" title={interviewee.publicPseudonym}>{interviewee.publicPseudonym}</p>
                    <p className="card-row">{interviewee.displacementYear ?? "Unknown year"}</p>
                    <p className="card-row" title={interviewee.currentContext ?? "Context pending"}>{interviewee.currentContext ?? "Context pending"}</p>
                  </div>
                </button>
              ))}
            {interviewees.length === 0 ? <p className="small-paragraph">{isIntervieweesLoading ? "Loading protected-preview survivors..." : "No protected-preview survivors are linked to this village yet."}</p> : null}
          </div>
          {canLoadMoreInterviewees ? (
            <button type="button" className="secondary-button" onClick={onLoadMoreInterviewees} disabled={isLoadingMoreInterviewees}>
              {isLoadingMoreInterviewees ? "Loading more survivors..." : "Load more  survivors"}
            </button>
          ) : null}
        </article>

        <div className="village-incident-evidence-grid">
          <article className="box table-box fade-in">
            <h3>Incident Overview</h3>
            <div className="box-title-row-incident-overview">
              <span className="micro-copy">
              </span>
              {incidentOverview.showToggle ? (
                <button
                  type="button"
                  className="secondary-button is-inline"
                  onClick={onToggleIncidentOverview}
                  aria-expanded={incidentOverview.isExpanded}
                >
                  {incidentOverview.toggleLabel}
                </button>
              ) : null}
            </div>
            <div className={`incident-table${incidentOverview.isExpanded ? " is-expanded" : ""}`}>
              <table className="repo-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Interviewee</th>
                    <th>Incident Count</th>
                  </tr>
                </thead>
                <tbody>
                  {incidentEntries.length > 0 ? incidentEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.date}</td>
                      <td>{entry.interviewee}</td>
                      <td>{entry.incidentCount}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={3}>{hasPreviewMatch ? "No published incident summaries are available for this village yet." : "Reference-only villages do not expose incident summaries until they match a preview village."}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="box slim-box evidence-box fade-in">
            <h3>Evidence Repo</h3>
            {isEvidenceLoading ? (
              <p className="repo-line">Loading published evidence metadata...</p>
            ) : evidenceEntries.length > 0 ? evidenceEntries.map((entry) => (
              <p key={entry.id} className="repo-line">
                <strong>{entry.label}</strong>{entry.label.toLowerCase().includes(entry.type.toLowerCase()) ? "" : ` ${entry.type}`}
              </p>
            )) : (
              <p className="repo-line">{evidenceItems.length === 0 ? "No published evidence items for this village yet." : "Evidence metadata is still loading."}</p>
            )}
          </article>
        </div>
      </div>

      {backButton}
      </div>
    </section>
  );
}
